import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for Bluetooth barcode scanner integration
 * Most Bluetooth barcode scanners work by emulating keyboard input
 * This hook detects rapid keyboard input (typical of barcode scanners)
 * and captures the scanned barcode value
 */
export const useBarcodeScanner = (
	onScan: (barcode: string) => void,
	enabled: boolean = true,
) => {
	const [isScanning, setIsScanning] = useState(false);
	const inputBuffer = useRef<string>('');
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastKeyTimeRef = useRef<number>(0);

	useEffect(() => {
		if (!enabled) return;

		const handleKeyPress = (event: KeyboardEvent) => {
			// Ignore if user is typing in an input field (unless it's the barcode field)
			const target = event.target as HTMLElement;
			if (
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable
			) {
				// Check if it's the barcode input field
				const isBarcodeField =
					target.getAttribute('name') === 'barCode' ||
					target.getAttribute('id') === 'barCode-input';
				
				if (!isBarcodeField) {
					return; // Don't intercept if typing in other fields
				}
			}

			const currentTime = Date.now();
			const timeSinceLastKey = currentTime - lastKeyTimeRef.current;

			// Barcode scanners typically input characters very quickly (< 50ms between keys)
			// If more than 100ms has passed, assume it's a new scan
			if (timeSinceLastKey > 100) {
				inputBuffer.current = '';
				setIsScanning(true);
			}

			// Handle special keys
			if (event.key === 'Enter') {
				event.preventDefault();
				if (inputBuffer.current.length > 0) {
					onScan(inputBuffer.current.trim());
					inputBuffer.current = '';
					setIsScanning(false);
				}
				return;
			}

			// Accumulate characters
			if (event.key.length === 1) {
				inputBuffer.current += event.key;
				lastKeyTimeRef.current = currentTime;

				// Clear timeout if exists
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				// Set timeout to detect end of scan (no input for 200ms)
				timeoutRef.current = setTimeout(() => {
					if (inputBuffer.current.length > 0) {
						onScan(inputBuffer.current.trim());
						inputBuffer.current = '';
						setIsScanning(false);
					}
				}, 200);
			}
		};

		// Add event listener
		window.addEventListener('keypress', handleKeyPress);

		// Cleanup
		return () => {
			window.removeEventListener('keypress', handleKeyPress);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [enabled, onScan]);

	return { isScanning };
};

